import InputTypes from '../constants/editabletypes.constants'

/**
 * Checks if a single editable field is complete based on its value and configuration
 * @param {HTMLElement} editable - The DOM element representing the editable
 * @param {Object} editableLookup - The lookup object containing editable metadata
 * @returns {boolean} - True if the editable is complete, false otherwise
 */
export const isEditableComplete = (editable, editableLookup) => {
    if (!editable || !editableLookup) {
        return false
    }

    const inputTypeId = editableLookup?.inputTypeId
    const name = editableLookup?.name
    const value = editable.innerText
    const isMandatory = editableLookup?.isMandatory

    let completed = false
    if (isMandatory) {
        switch (inputTypeId) {
            case InputTypes.HtmlEditor:
                completed = value !== ''
                break

            default:
                const textValue = value?.toLowerCase().trim()
                const elementName = name?.toLowerCase().trim()
                if (textValue && textValue !== '') {
                    completed = textValue !== elementName
                }
        }
    }

    return completed
}

/**
 * Calculates document completion progress as a percentage
 * @param {HTMLElement[]} allEditables - Array of all editable DOM elements
 * @param {Object} editableLookups - Lookup object mapping editable IDs to their metadata
 * @param {boolean} userCanEditFullDocument - Whether user has full document edit permissions
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateDocumentProgress = (
    allEditables,
    editableLookups,
    userCanEditFullDocument
) => {
    if (userCanEditFullDocument) {
        return 100
    }

    if (!allEditables.length || !Object.keys(editableLookups).length) {
        return 100
    }

    let nonMandatoryCount = 0

    const completedMandatoryEditables = allEditables.filter(editable => {
        const editableId = editable.getAttribute('data-tag-id')
        const ariaInvalid = editable.getAttribute('aria-invalid')

        if (ariaInvalid === 'true') {
            return false
        }

        const editableLookup = editableLookups[editableId]
        if (!editableLookup) {
            console.error(
                `editable ID: ${editableId} does not exist but is present in the document!`
            )
            return false
        }

        const isMandatory = editableLookup?.isMandatory

        if (!isMandatory) {
            ++nonMandatoryCount
        }

        if (isEditableComplete(editable, editableLookup)) {
            return editableId
        }
    })

    const totalMandatoryEditables = allEditables.length - nonMandatoryCount

    if (totalMandatoryEditables === 0) {
        return 100
    }

    const progress = Math.round(
        (100 / totalMandatoryEditables) * completedMandatoryEditables.length
    )

    return isNaN(progress) ? 0 : progress
}
